import { useState, useEffect } from 'react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: string;
}

// CSS for HTML content rendering
const htmlContentStyles = `
  .privacy-policy-content h1,
  .privacy-policy-content h2,
  .privacy-policy-content h3,
  .privacy-policy-content h4,
  .privacy-policy-content h5,
  .privacy-policy-content h6 {
    font-weight: 700;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    line-height: 1.25;
  }
  .privacy-policy-content h1 { font-size: 2rem; color: #1f2937; }
  .privacy-policy-content h2 { font-size: 1.5rem; color: #374151; }
  .privacy-policy-content h3 { font-size: 1.25rem; color: #4b5563; }
  .privacy-policy-content h4,
  .privacy-policy-content h5,
  .privacy-policy-content h6 { font-size: 1rem; color: #4b5563; }
  .privacy-policy-content p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
  .privacy-policy-content ul,
  .privacy-policy-content ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }
  .privacy-policy-content li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
  .privacy-policy-content strong,
  .privacy-policy-content b {
    font-weight: 700;
    color: #1f2937;
  }
  .privacy-policy-content em,
  .privacy-policy-content i {
    font-style: italic;
  }
  .privacy-policy-content blockquote {
    border-left: 4px solid #667eea;
    padding-left: 1rem;
    margin-left: 0;
    margin-bottom: 1rem;
    color: #6b7280;
    font-style: italic;
  }
`;

export default function PrivacyPolicyModal({ isOpen, onClose, tenantId: tenantIdProp }: PrivacyPolicyModalProps) {
  const [policy, setPolicy] = useState<{ version: string; content: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchPrivacyPolicy();
    }
  }, [isOpen]);

  const fetchPrivacyPolicy = async () => {
    try {
      setLoading(true);
      setError('');

      // Resolve tenantId: prop > localStorage access_token payload > skip
      let tenantId = tenantIdProp;
      if (!tenantId) {
        const token = localStorage.getItem('access_token');
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            tenantId = payload.tenantId;
          } catch { /* ignore */ }
        }
      }

      const url = tenantId
        ? `/api/gdpr/privacy-policy?tenantId=${encodeURIComponent(tenantId)}`
        : '/api/gdpr/privacy-policy';

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch privacy policy');
      }

      const result = await response.json();
      setPolicy(result.data);
    } catch (err) {
      setError('Unable to load privacy policy. Please try again later.');
      console.error('Privacy policy fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{htmlContentStyles}</style>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Privacy Policy</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded p-1"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700">
              {error}
            </div>
          ) : policy ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <strong>Version:</strong> {policy.version}
              </div>
              <div className="privacy-policy-content text-gray-700">
                <div 
                  dangerouslySetInnerHTML={{ __html: policy.content }}
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
